/* Supply › Reports › Sales › Sales Register
 * Sales invoices over a date range, by channel and payment status.
 * Data: GET billing/invoices (mapped invoice rows). */
import React, { useEffect, useMemo, useState } from "react";
import supplyService from "../../../Services/supplyService";
import { cbFmtINR, cbFmtNum, cbStatusVariant } from "../supplyData";
import {
  Button,
  Pill,
  Input,
  Select,
  Table,
  Tr,
  Td,
  SectionHeader,
  ToolbarBar,
  DateRangeFilter,
  KpiBox,
  LoadingState,
  ErrorState,
  IconDownload,
} from "../supplyKit";
import { exportToXlsx, inDateRange } from "./reportUtils";
import { ReportBack } from "./reportShared";
import { PHARMACY_ROUTES } from "../../../Route/path";

export const KIND_LABEL = { b2c: "B2C", che: "CHE / Engage", eyewear: "Eyewear", b2b: "B2B" };

const SalesRegister = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState({ q: "", kind: "", status: "", fromDate: "", toDate: "", sortDir: "desc" });
  const setF = (key, val) => setFilters((f) => ({ ...f, [key]: val }));

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    supplyService
      .getInvoices()
      .then((rows) => {
        if (!alive) return;
        setInvoices(Array.isArray(rows) ? rows : []);
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || "Could not load sales report.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [reloadKey]);

  useEffect(() => {
    setPage(0);
  }, [filters, pageSize]);

  const filtered = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    const out = invoices.filter((inv) => {
      if (filters.kind && inv.kind !== filters.kind) return false;
      if (filters.status && inv.status !== filters.status) return false;
      if (!inDateRange(inv.issued, filters.fromDate, filters.toDate)) return false;
      if (q) {
        const hay = [inv.id, inv.customerName].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    out.sort((a, b) =>
      filters.sortDir === "asc"
        ? String(a.issued).localeCompare(String(b.issued))
        : String(b.issued).localeCompare(String(a.issued))
    );
    return out;
  }, [invoices, filters]);

  const totals = useMemo(
    () =>
      filtered.reduce(
        (acc, inv) => {
          acc.amount += Number(inv.amount) || 0;
          acc.paid += Number(inv.paid) || 0;
          return acc;
        },
        { amount: 0, paid: 0 }
      ),
    [filtered]
  );
  const outstanding = totals.amount - totals.paid;

  const totalRows = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pagedRows = filtered.slice(safePage * pageSize, safePage * pageSize + pageSize);
  const startRow = totalRows === 0 ? 0 : safePage * pageSize + 1;
  const endRow = Math.min(totalRows, safePage * pageSize + pageSize);

  const statuses = useMemo(() => [...new Set(invoices.map((i) => i.status).filter(Boolean))], [invoices]);

  const handleExport = () => {
    const headers = ["Invoice", "Customer", "Channel", "Issued", "Due", "Amount", "Paid", "Outstanding", "Status"];
    const rows = filtered.map((inv) => [
      inv.id,
      inv.customerName || "",
      KIND_LABEL[inv.kind] || inv.kind || "",
      inv.issued || "",
      inv.due || "",
      Number((Number(inv.amount) || 0).toFixed(2)),
      Number((Number(inv.paid) || 0).toFixed(2)),
      Number(((Number(inv.amount) || 0) - (Number(inv.paid) || 0)).toFixed(2)),
      inv.status || "",
    ]);
    exportToXlsx("sales-register.xlsx", headers, rows, "Sales");
  };

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <ReportBack to={PHARMACY_ROUTES.SALES_REPORT} label="Sales Reports" />
      <SectionHeader
        title="Sales Register"
        sub="Sales invoices by channel, date, and payment status"
        actions={
          <Button icon={<IconDownload size={14} />} onClick={handleExport} disabled={!filtered.length}>
            Export
          </Button>
        }
      />

      <ToolbarBar>
        <Input
          value={filters.q}
          onChange={(e) => setF("q", e.target.value)}
          placeholder="Invoice or customer…"
          style={{ width: 240, maxWidth: "100%" }}
        />
        <Select value={filters.kind} onChange={(e) => setF("kind", e.target.value)}>
          <option value="">All channels</option>
          {Object.entries(KIND_LABEL).map(([k, label]) => (
            <option key={k} value={k}>
              {label}
            </option>
          ))}
        </Select>
        <Select value={filters.status} onChange={(e) => setF("status", e.target.value)}>
          <option value="">All statuses</option>
          {statuses.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>
        <DateRangeFilter
          from={filters.fromDate}
          to={filters.toDate}
          onFromChange={(v) => setF("fromDate", v)}
          onToChange={(v) => setF("toDate", v)}
        />
        <Select value={filters.sortDir} onChange={(e) => setF("sortDir", e.target.value)} title="Sort by date">
          <option value="desc">Newest first</option>
          <option value="asc">Oldest first</option>
        </Select>
        <Button onClick={() => setFilters({ q: "", kind: "", status: "", fromDate: "", toDate: "", sortDir: "desc" })}>
          Reset
        </Button>
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {cbFmtNum(totalRows)} invoices
        </div>
      </ToolbarBar>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" style={{ display: "grid", gap: 10 }}>
        <KpiBox label="Invoices" value={cbFmtNum(totalRows)} variant="ink" />
        <KpiBox label="Sales value" value={cbFmtINR(totals.amount)} variant="green" />
        <KpiBox label="Collected" value={cbFmtINR(totals.paid)} variant="ink" />
        <KpiBox label="Outstanding" value={cbFmtINR(outstanding)} variant={outstanding > 0 ? "amber" : "ink"} />
      </div>

      {loading ? (
        <LoadingState label="Loading sales register…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : (
        <>
          <Table
            columns={[
              { label: "Invoice" },
              { label: "Customer" },
              { label: "Channel" },
              { label: "Issued" },
              { label: "Due" },
              { label: "Amount", align: "right" },
              { label: "Paid", align: "right" },
              { label: "Outstanding", align: "right" },
              { label: "Status" },
            ]}
          >
            {pagedRows.length === 0 && (
              <tr>
                <Td colSpan={9} style={{ textAlign: "center", color: "var(--fg-muted)", height: 80 }}>
                  No invoices match these filters.
                </Td>
              </tr>
            )}
            {pagedRows.map((inv) => {
              const due = (Number(inv.amount) || 0) - (Number(inv.paid) || 0);
              return (
                <Tr key={inv.id}>
                  <Td mono style={{ fontWeight: 600 }}>
                    {inv.id}
                  </Td>
                  <Td>{inv.customerName}</Td>
                  <Td>
                    <Pill variant="neutral">{KIND_LABEL[inv.kind] || inv.kind}</Pill>
                  </Td>
                  <Td mono>{inv.issued}</Td>
                  <Td mono style={{ color: "var(--fg-muted)" }}>
                    {inv.due}
                  </Td>
                  <Td mono right style={{ fontWeight: 600 }}>
                    {cbFmtINR(inv.amount)}
                  </Td>
                  <Td mono right>
                    {cbFmtINR(inv.paid)}
                  </Td>
                  <Td mono right style={{ color: due > 0 ? "var(--amber-700)" : "var(--fg-muted)" }}>
                    {cbFmtINR(due)}
                  </Td>
                  <Td>
                    <Pill variant={cbStatusVariant(inv.status)} dot>
                      {inv.status}
                    </Pill>
                  </Td>
                </Tr>
              );
            })}
          </Table>

          <ToolbarBar>
            <Select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
              {[20, 50, 100, 200].map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </Select>
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
                {startRow}–{endRow} of {cbFmtNum(totalRows)}
              </span>
              <Button size="sm" disabled={safePage <= 0} onClick={() => setPage(Math.max(0, safePage - 1))}>
                ← Prev
              </Button>
              <Button size="sm" disabled={safePage + 1 >= totalPages} onClick={() => setPage(safePage + 1)}>
                Next →
              </Button>
            </div>
          </ToolbarBar>
        </>
      )}
    </div>
  );
};

export default SalesRegister;
